import { Injectable } from '@angular/core';
import { AuthService } from '@vinnova/auth';
import { TranslateService } from '@vinnova/translation';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChatMessage } from '../models/chatbot.model';
import { ChatApiService, Citations } from './chat-api.service';

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private _isOpen = new BehaviorSubject<boolean>(false);
  private _messages = new BehaviorSubject<ChatMessage[]>([]);
  private _isTyping = new BehaviorSubject<boolean>(false);
  private _isAvailable = new BehaviorSubject<boolean>(false);
  private _personName = new BehaviorSubject<string>('');

  public isOpen$ = this._isOpen.asObservable();
  public messages$ = this._messages.asObservable();
  public isTyping$ = this._isTyping.asObservable();
  public isAvailable$ = this._isAvailable.asObservable();
  public personName$ = this._personName.asObservable();
  private conversationId: string | undefined = undefined;

  constructor(
    private authService: AuthService,
    private chatApiService: ChatApiService,
    private translateService: TranslateService,
  ) {
    // Check chatbot availability on initialization
    this.chatApiService.getAvailability().subscribe({
      next: (isAvailable) => {
        this._isAvailable.next(isAvailable);
      },
      error: (error) => {
        console.error('Error checking chatbot availability:', error);
        this._isAvailable.next(false);
      },
    });

    // Get person details for greeting
    this.authService.getPersonDetails().subscribe((person) => {
      this._personName.next(person?.firstName || '');
    });
  }

  open(): void {
    this._isOpen.next(true);
  }

  close(): void {
    this._isOpen.next(false);
    this.conversationId = undefined; // Reset the conversation ID when the chat is closed
    this.clearMessages();
  }

  toggle(): void {
    this._isOpen.next(!this._isOpen.value);
    if (this._isOpen.value) {
      this.open();
    } else if (!this._isOpen.value) {
      this.close();
    }
  }

  // Lägg tillanvändarens meddelande utan att skicka till api
  sendUserMessage(content: string): void {
    if (!content.trim()) {
      return;
    }
    const userMessage: ChatMessage = {
      content: content,
      sender: 'user',
      type: 'text',
    };
    this.addMessage(userMessage);

    this._isTyping.next(true);
  }

  // kalla på api
  sendBotMessage(content: string): Observable<ChatMessage> {
    return new Observable((observer) => {
      this.chatApiService
        .sendMessage(
          content,
          this.conversationId,
          this.translateService.activeLang,
        )
        .subscribe({
          next: (response) => {
            this._isTyping.next(false);
            this.conversationId = response.conversationId; // Save the conversation ID;
            const botMessage = this.addBotMessage(
              response.message,
              'normal',
              response.citations,
            );
            observer.next(botMessage);
            observer.complete();
          },
          error: (error) => {
            console.error('Error sending bot message:', error);
            this._isTyping.next(false);
            observer.error(error);
          },
        });
    });
  }

  clearMessages(): void {
    this._messages.next([]);
  }

  stopTyping(): void {
    this.chatApiService.cancelOngoingRequest();
    this._isTyping.next(false);
  }

  private addMessage(message: ChatMessage): void {
    const messages = [...this._messages.value, message];
    this._messages.next(messages);
  }

  private addBotMessage(
    content: string,
    messageType: 'greeting' | 'welcome' | 'normal' = 'normal',
    citations?: Citations[],
  ): ChatMessage {
    const message: ChatMessage = {
      content: content,
      sender: 'bot',
      type: 'text',
      messageType: messageType,
      citations: citations,
    };
    this.addMessage(message);
    return message;
  }
}
